/**
 * Страница закрытия popup после входа через Маркбэйс id на auth.markbase.ru.
 * Открывается в popup по return_url после успешного логина/регистрации.
 *
 * Критично: итоговую навигацию выполняет только opener после подтверждённого /api/auth/me.
 * Popup лишь уведомляет opener и закрывается — без ухода в каталог/dashboard внутри окна.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Spin } from 'antd';
import {
  AUTH_BROADCAST_CHANNEL,
  closeSelfAuthPopup,
  isAuthPopupWindow,
} from './authPopup';

const AUTH_MESSAGE_TYPE = 'wsid:auth_success';

const isSafeReturnTarget = (url) => {
  if (!url) return false;
  if (url.startsWith('/')) return true;
  try {
    const parsed = new URL(url);
    return parsed.hostname === 'markbase.ru' || parsed.hostname.endsWith('.markbase.ru');
  } catch (_) {
    return false;
  }
};

/** Полный URL для редиректа (совместимо с Login.jsx isSafeReturnUrl / buildRedirectTarget) */
const buildSafeRedirectUrl = (to) => {
  const raw = (to || '/dashboard').trim() || '/dashboard';
  if (raw.startsWith('http')) {
    if (!isSafeReturnTarget(raw)) return `${window.location.origin}/dashboard`;
    return raw;
  }
  const path = raw.startsWith('/') ? raw : `/${raw}`;
  return `${window.location.origin}${path}`;
};

export default function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const [done, setDone] = useState(false);
  const [stuckOpen, setStuckOpen] = useState(false);

  useEffect(() => {
    const toParam = searchParams.get('to') || '/dashboard';
    const flow = searchParams.get('wsid_flow') || searchParams.get('flow') || null;
    const targetUrl = buildSafeRedirectUrl(toParam);
    const opener = window.opener;
    const hasOpener = Boolean(opener && !opener.closed);
    const asPopup = hasOpener || isAuthPopupWindow();

    const payload = { type: AUTH_MESSAGE_TYPE, to: toParam, flow };

    const notifyOpener = () => {
      if (hasOpener) {
        try {
          opener.postMessage(payload, window.location.origin);
        } catch (e) {
          console.warn('AuthCallback: postMessage не выполнен:', e);
        }
      }
      try {
        const ch = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
        ch.postMessage(payload);
        ch.close();
      } catch (_) {
        /* BroadcastChannel may be unavailable */
      }
    };

    notifyOpener();

    if (asPopup) {
      if (hasOpener) {
        try {
          opener.focus();
        } catch (e) {
          console.warn('AuthCallback: opener.focus не выполнен:', e);
        }
      }
      // Не делаем location.replace в popup — иначе окно уезжает в каталог/app.
      closeSelfAuthPopup();
      const t1 = setTimeout(closeSelfAuthPopup, 80);
      const t2 = setTimeout(closeSelfAuthPopup, 400);
      const t3 = setTimeout(() => {
        // Если браузер запретил close — покажем ручное закрытие, без навигации в app.
        if (!window.closed) setStuckOpen(true);
      }, 900);
      setDone(true);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
      };
    }

    // Нет opener и это не именованный popup (прямой заход) — редирект в этом же окне
    window.location.replace(targetUrl);
    setDone(true);
  }, [searchParams]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: '#f5f5f5',
      fontFamily: 'system-ui, sans-serif',
      padding: 24,
      textAlign: 'center',
    }}>
      {!stuckOpen ? <Spin size="large" /> : null}
      <p style={{ marginTop: 16, color: '#666', maxWidth: 360 }}>
        {stuckOpen
          ? 'Вход выполнен. Это окно можно закрыть — продолжите работу во вкладке приложения.'
          : (done ? 'Вход выполнен. Закрытие окна…' : 'Завершение входа…')}
      </p>
      {stuckOpen ? (
        <button
          type="button"
          onClick={() => closeSelfAuthPopup()}
          style={{
            marginTop: 12,
            padding: '10px 18px',
            borderRadius: 8,
            border: '1px solid #d9d9d9',
            background: '#fff',
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          Закрыть окно
        </button>
      ) : null}
    </div>
  );
}

export { AUTH_MESSAGE_TYPE };
